import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormBuilder, FormArray, FormControl } from '@angular/forms';
import { Practice } from '../../../../shared/models/practice';
import { NomenclatorFacade } from '../../../../abstraction/nomenclator.facade';
import { ProvisionsFrequencyUnit } from '../../../../core/enums/provisionsFrequencyUnit';
import { CustomValidators } from '../../../../core/validators/custom-validators';
import { Subscription } from 'rxjs';
import { Arrangement } from 'src/app/shared/models/arrangement';
import { ArrangementsFacade } from 'src/app/abstraction/arrangements.facade';
import { AlertService } from '../../../../core/services/alert.service';
import { PostProvisionBody } from 'src/app/core/services/provisions.service';
import { isPracticeCountedByHours } from 'src/app/core/utils/practice-restriction.utils';
import moment from 'moment';
import { CaseModule } from 'src/app/shared/models/module';
import { CaseModulePostQPS, CaseModuleRequisitionPostQPS } from 'src/app/shared/models/modulesQPS';
import { ExperienceI18nService } from 'src/app/core/experience/experience-i18n.service';

@Component({
    selector: 'app-admissions-provisions',
    templateUrl: './admissions-provisions.component.html',
    styleUrls: ['./admissions-provisions.component.scss']
})
export class AdmissionsProvisionsComponent implements OnInit {

    public readonly PRACTICE = 'practiceId';
    public readonly PRACTICE_SEARCHING = 'practiceSearching';
    public readonly AMOUNT = 'amount';
    public readonly ALLOWED_HOURS = 'allowedHours';
    public readonly UNIT = 'unit';
    public readonly DURATION_AMOUNT = 'durationAmount';
    public readonly DURATION_UNIT = 'durationUnit';
    public readonly FROM_DATE = 'fromDate';
    public readonly TO_DATE = 'toDate';
    public readonly TOTAL_AMOUNT = 'totalAmount';
    public readonly ARRANGEMENT = 'arrangement';
    public readonly ARRANGEMENT_SEARCHING = 'arrangementSeaarching';

    @Input() screenCalling: string = 'ADMISSIONS';
    @Input() provisionsItemsPreselected: PostProvisionBody[];
    @Input() financierIdSelected: number;
    @Output() provisionsData = new EventEmitter<PostProvisionBody[]>();
    @Output() formProvisionsDataValid = new EventEmitter<boolean>();
    @Output() arrangementsData = new EventEmitter<Arrangement>();
    @Output() caseModulesData = new EventEmitter<CaseModuleRequisitionPostQPS[]>();
    @Output() formModulesDataValid = new EventEmitter<boolean>();

    btn_addPractice_disabled: boolean = false;
    lastProvisionData: PostProvisionBody[] = [];
    practices: Practice[];
    practicesFiltered: Practice[];
    arrangements: Arrangement[];
    activeArrangements: Arrangement[];
    private lastProvisionSnapshot = '';
    provisionsFrequencyUnit: { id: string, name: string } [];
    isLoadingGettingPractices: boolean; _isLoadingGettingPractices: Subscription;
    arrangementForm: FormGroup;
    _arrangements: Subscription;
    practicesForm: FormGroup;
    _practices: Subscription;
    arrangementSelected: Arrangement;
    modulesDate: CaseModulePostQPS[];

    public get practiceS(): FormArray {
        return this.practicesForm.get("practiceS") as FormArray
    }

    constructor(
        private fb: FormBuilder,
        private nomenclatorFacade: NomenclatorFacade,
        private arrangementsFacade: ArrangementsFacade,
        private alertService: AlertService,
        private experienceI18n: ExperienceI18nService
    ) { }

    ngOnInit(): void {
        this._practices = this.nomenclatorFacade.getOrLoadPractices$({ active: true }).subscribe( practices => {
            this.practices = practices?.filter((practice) => !practice.specialty?.isSupply);
            this.practicesFiltered = this.practices;
        })

        this._arrangements = this.arrangementsFacade.getArrangements$().subscribe( arrangements => {
            this.arrangements = arrangements;
            this.activeArrangements = arrangements?.filter(arr => !!arr.active);
        })

        this.arrangementForm = this.createArrangementForm();

        this.practicesForm = this.fb.group({
            practiceS: this.fb.array([])
        });

        this._isLoadingGettingPractices = this.nomenclatorFacade.isLoadingGetPractices$().subscribe(loading => this.isLoadingGettingPractices = loading);

        // PRACTICES
        this.practicesForm.statusChanges.subscribe(formPractices => {
            this.btn_addPractice_disabled = formPractices === 'INVALID' ? true : false;
        })

        this.practicesForm.valueChanges.subscribe(() => {

            if (this.practicesForm.status === 'VALID') {
              let data: PostProvisionBody[] = [];
              data = this.practicesForm.value.practiceS.map(
                ({ practiceSearching, radioButton, ...newPractice }) => {
                  const frequency = {
                    amount: newPractice['amount'],
                    unit: newPractice['unit'],
                    allowedHours: newPractice['allowedHours']
                  };

                  newPractice = { ...newPractice, frequency }; // Add frequency
                  const { amount, unit, allowedHours, ...editNewPractice } = newPractice;
                  return editNewPractice;
                }
              );

              const snapshot = JSON.stringify(data);

              if (snapshot !== this.lastProvisionSnapshot) {
                this.lastProvisionSnapshot = snapshot;
                this.lastProvisionData = data;
                this.provisionsData.emit(data);
              }

              this.formProvisionsDataValid.emit(true);

            } else {
              const emptySnapshot = '[]';
              if (this.lastProvisionSnapshot !== emptySnapshot) {
                this.lastProvisionSnapshot = emptySnapshot;
                this.lastProvisionData = [];
                this.provisionsData.emit([]);
              }
              this.formProvisionsDataValid.emit(false);
            }
          });

        this.provisionsFrequencyUnit = [
            {
                id: ProvisionsFrequencyUnit.DIA,
                name: this.experienceI18n.translate('modules.admissions-provisions.frequency-unit.dia', undefined, 'Dia'),
            },
            {
                id: ProvisionsFrequencyUnit.SEMANA,
                name: this.experienceI18n.translate('modules.admissions-provisions.frequency-units.semana', undefined, 'Semana'),
            },
            {
                id: ProvisionsFrequencyUnit.MES,
                name: this.experienceI18n.translate('modules.admissions-provisions.frequency-units.mes', undefined, 'Mes'),
            }
        ];


        this.addPractice();
    }

    ngOnChanges(): void {
        if (!!this.provisionsItemsPreselected && this.provisionsItemsPreselected.length > 0) {

          if (!this.practicesForm) {
            return;
          }

          while (this.practiceS.length !== 0) {
            this.practiceS.removeAt(0);
          }

          this.provisionsItemsPreselected.forEach(provItem => {
            const practiceGroup = this.newPractice();

            practiceGroup.patchValue({
              [this.PRACTICE]: provItem.practiceId,
              [this.AMOUNT]: provItem.frequency?.amount,
              [this.ALLOWED_HOURS]: provItem.frequency?.allowedHours,
              [this.UNIT]: provItem.frequency?.unit,
              [this.DURATION_AMOUNT]: provItem?.durationAmount,
              [this.TOTAL_AMOUNT]: provItem?.totalAmount,
              [this.DURATION_UNIT]: provItem?.durationUnit,
              [this.FROM_DATE]: moment(provItem?.fromDate),
              [this.TO_DATE]: moment(provItem?.toDate),
            });

            const isDuration = !!provItem.durationAmount || !!provItem.durationUnit;
            practiceGroup.get('radioButton')?.setValue(isDuration ? 'duration' : 'date');

            if (!isDuration) {
              practiceGroup.get('fromDate')?.setValue(provItem.fromDate);
              practiceGroup.get('toDate')?.setValue(provItem.toDate);
            }

            this.practiceS.push(practiceGroup);
          });
        }
      }


    createArrangementForm(){
        return this.fb.group({
            [this.ARRANGEMENT]: [],
            [this.ARRANGEMENT_SEARCHING]: [],
        })
    }

    newPractice(): FormGroup {
        return this.fb.group({
            [this.PRACTICE]: ['', [CustomValidators.required(this.experienceI18n.translate('modules.admissions-provisions.practice-form-group.required-practice', undefined, 'Práctica requerida'))]],
            [this.PRACTICE_SEARCHING]: [''],
            [this.AMOUNT]: ['',[CustomValidators.number(this.experienceI18n.translate('modules.admissions-provisions.practice-form-group.invalid-amount', undefined, 'Valor incorrecto'))]], // [ CustomValidators.required('Frecuencia requerida') ]
            [this.ALLOWED_HOURS]: ['', ], // [ CustomValidators.required('Frecuencia requerida') ]
            [this.UNIT]: [''], // [ CustomValidators.required('Periodo requerido') ]
            radioButton: new FormControl({ value: 'duration', disabled: false }, ), // [CustomValidators.required('Seleccione Duración o Fecha')]
            [this.DURATION_AMOUNT]: ['', [CustomValidators.number(this.experienceI18n.translate('modules.admissions-provisions.practice-form-group.invalid-amount', undefined, 'Valor incorrecto'))] ],    // By default // [CustomValidators.required('Cantidad requerida')]
            [this.DURATION_UNIT]: ['', ],       // By default // [CustomValidators.required('Periodo requerido')]
            [this.FROM_DATE]: [''],
            [this.TO_DATE]: [''],
            [this.TOTAL_AMOUNT]: ['', ], // [CustomValidators.required('Cantidad requerida')]
        })
    }

    addPractice() {
        this.practiceS.push(this.newPractice());
    }

    removePractice(i: number) {

        if (this.practiceS['controls'].length == 1) {
            // this.practiceS['controls'][i].reset();
            // this.practiceS['controls'][i]['controls'].radioButton.value = 'duration';
            this.practiceS.removeAt(i); // Else radiobutton fail
            this.practiceS.push(this.newPractice());
        } else {
            this.practiceS.removeAt(i);
        }
    }

    calculateIdPractice(element: Practice) {
        return element ? element.id : null;
    }

    // It's necessary arrow function
    calculateNamePractice = (element: Practice) => {
      if(!!this.arrangementSelected) {
        let practices = this.arrangementSelected?.practiceArrangements?.filter(pr => pr.practiceId === element.id);
        let practice = practices[practices?.length - 1]

        if(!!practice?.code) {
            return element ? element.name + ' #' + practice.code : null;
        }
        return element ? element.name + ' #' + element.code : null;
      }
      return element ? element.name + ' #' + element.code : null;
    }

    calculateIdArrangement(element: Arrangement) {
        return element ? element.id : null;
    }
    calculateNameArrangement(element: Arrangement) {
        return element ? element.name : null;
    }

    shouldShowAllowedHours(index: number): boolean {
      const practiceId = this.getPracticeSFormGroup(index)?.get(this.PRACTICE)?.value;

      if (!practiceId) {
        return false;
      }

      const practice = this.practices?.find(p => p.id == practiceId)
        || this.practicesFiltered?.find(p => p.id == practiceId);

      return isPracticeCountedByHours(practice?.restriction);
    }

    clickRadiobutton(i: number, event: any) {
        const group = this.practiceS.at(i) as FormGroup;

        switch (event.value) {
          case 'duration':
            group.patchValue({
              [this.FROM_DATE]: null,
              [this.TO_DATE]: null,
            });
            break;

          case 'date':
            group.patchValue({
              [this.DURATION_AMOUNT]: null,
              [this.DURATION_UNIT]: null,
            });
            break;
        }
      }


    ngOnDestroy(): void {
        //Called once, before the instance is destroyed.
        //Add 'implements OnDestroy' to the class.
        this._practices.unsubscribe();
    }

    onSelectArrangement(event){
        if (!!event){
            if (!this.arrangementSelected || (!!this.arrangementSelected && this.validateChangeArrangement(event))){
                let arrangement = this.arrangements.find(arr => arr.id == event);
                if (!arrangement){
                    return;
                }
                if (!!arrangement.arrangementId && (!arrangement.practiceArrangements || arrangement.practiceArrangements.length == 0)){
                    let arrangementRelated = this.arrangements.find(arr => arr.id == arrangement.arrangementId);
                    if (!!arrangementRelated){
                        const practicesIds = Array.from(new Set(arrangementRelated?.practiceArrangements?.filter( arrDetail => arrDetail.active ).map(arrDetail => arrDetail.practiceId) ?? [])) // Filter practices active=1
                        this.practicesFiltered = (this.practices ?? []).filter(practice => practicesIds.includes(practice.id));
                    }
                } else {
                    const practicesIds = Array.from(new Set(arrangement?.practiceArrangements?.filter( arrDetail => arrDetail.active ).map( arrDetail => arrDetail.practiceId ) ?? [])) // Filter practices active=1
                    this.practicesFiltered = (this.practices ?? []).filter(practice => practicesIds.includes(practice.id));
                }
                this.arrangementSelected = arrangement;
                this.arrangementsData.emit(this.arrangementSelected);
            } else {
                this.alertService.openInfo(this.experienceI18n.translate('modules.admissions-provisions.select-arrangement.info-alert', undefined, "El convenio no incluye una o más de las prácticas seleccionadas. Corrija el campo de prácticas y vuelva a intentar"));
                this.arrangementForm.get(this.ARRANGEMENT).setValue(this.arrangementSelected.id.toString());
            }
        } else {
            this.practicesFiltered = this.practices;
            this.arrangementSelected = undefined;
            this.arrangementsData.emit(this.arrangementSelected);
        }
    }

    validateChangeArrangement(event){
        const practicesSelected = this.practicesForm.value.practiceS.map(control => control.practiceId).filter(pid => !!pid && pid != '');
        const arrangement = this.arrangements.find(arr => arr.id == event);
        if (!arrangement){
            return true;
        }
        let letChangeArrangement = true;
        practicesSelected?.forEach(ps => {
            if (!arrangement.practiceArrangements?.find(pa => pa.practiceId == ps)){
                letChangeArrangement = false;
            }
        })
        return letChangeArrangement;
    }

    getPracticeSFormGroup(index: number): FormGroup {
        return this.practiceS.at(index) as FormGroup;
    }

    modulesData( modulesData: PostProvisionBody[] ) {
        console.log("Datos de los módulos: ", modulesData);
        //this.postProvisionBodyPractices = modulesData
    }

    formModulesValid( event:boolean ) {
        console.log("Form del módulo válido?: ", event);
        this.formModulesDataValid.emit(event);
    }

    getCaseModulesData( modulesData: CaseModule[] ) {
        console.log("Datos de los módulos: ", modulesData);
        let caseModulesToRequisitionPost = this.caseModuleDataMapper(modulesData);
        this.caseModulesData.emit(caseModulesToRequisitionPost);
    }

    caseModuleDataMapper( modulesData: CaseModule[]): CaseModuleRequisitionPostQPS[] {
        return modulesData.map(({ moduleId, ...module }) => ({
            moduleId,
            module
        }));
    }
}
